import { useMemo, useState } from 'react';
import type { AutoAnalyzerReport, RegionReport } from '../../analytics/auto-analyzer.ts';
import { AUTO_ANALYZER_QUICK_TARGETS, defaultSuggestedMaxGames } from '../../analytics/auto-analyzer.ts';
import {
  AUTO_ANALYZER_MAX_GAMES,
  AUTO_ANALYZER_MAX_MARTINGALE_GAMES,
} from '../../shared/constants.ts';
import { formatDecimal, formatInteger, formatPercent, formatSignedUnits, formatTarget } from '../../shared/format.ts';
import { formatProfitExposureRatio } from '../../analytics/martingale-grid.ts';
import { parseTargetMultiplierInput } from '../../shared/schemas.ts';
import { fetchJson } from '../lib/api.ts';
import { StatusMessage } from '../components/StatusMessage.tsx';

function defaultMaxGames(target: number): number {
  return defaultSuggestedMaxGames(target);
}

export function AutoAnalyzerPage() {
  const [targetInput, setTargetInput] = useState('20');
  const [maxGamesInput, setMaxGamesInput] = useState('20');
  const [result, setResult] = useState<AutoAnalyzerReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const parsed = useMemo(() => {
    try {
      const targetMultiplier = parseTargetMultiplierInput(targetInput);
      const maxGames = Number(maxGamesInput);
      if (!Number.isInteger(maxGames) || maxGames < 1 || maxGames > AUTO_ANALYZER_MAX_GAMES) {
        return {
          error: `Maximum games must be an integer from 1 to ${AUTO_ANALYZER_MAX_GAMES}.` as string | null,
          targetMultiplier,
          maxGames,
        };
      }
      if (targetMultiplier === 2 && maxGames > AUTO_ANALYZER_MAX_MARTINGALE_GAMES) {
        return {
          error: `Martingale maximum games cannot exceed ${AUTO_ANALYZER_MAX_MARTINGALE_GAMES} actual wagers.` as string | null,
          targetMultiplier,
          maxGames,
        };
      }
      if (targetMultiplier < 2) {
        return { error: 'Target must be at least 2x.', targetMultiplier, maxGames };
      }
      return { error: null as string | null, targetMultiplier, maxGames };
    } catch (cause) {
      return {
        error: cause instanceof Error ? cause.message : 'Invalid target',
        targetMultiplier: Number.NaN,
        maxGames: Number.NaN,
      };
    }
  }, [targetInput, maxGamesInput]);

  async function analyze() {
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<AutoAnalyzerReport>('/api/analytics/auto-analyzer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetMultiplier: parsed.targetMultiplier,
          maxGames: parsed.maxGames,
        }),
      });
      setResult(body);
      setDetailsOpen(false);
    } catch (cause) {
      setResult(null);
      setError(cause instanceof Error ? cause.message : 'Analysis failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold tracking-wide">Automatic Strategy Analyzer</h1>
        <p className="text-sm text-muted">
          Historical research only. Enter a target and a maximum number of games. The analyzer searches entry
          drought and duration from history. It does not change the paper simulation.
        </p>
      </header>

      <form
        className="grid gap-3 rounded border border-line bg-panel p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          void analyze();
        }}
      >
        <label className="block text-sm">
          <span className="text-xs uppercase tracking-wide text-muted">Target</span>
          <input
            value={targetInput}
            onChange={(event) => {
              setTargetInput(event.target.value);
              try {
                const next = parseTargetMultiplierInput(event.target.value);
                if (Number.isInteger(next) && next >= 2) {
                  setMaxGamesInput(String(defaultMaxGames(next)));
                }
              } catch {
                // Keep the current Max Games while the target field is incomplete.
              }
            }}
            inputMode="decimal"
            aria-label="Target"
            className="mt-1 w-full rounded border border-line bg-page px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs uppercase tracking-wide text-muted">Maximum games</span>
          <input
            value={maxGamesInput}
            onChange={(event) => setMaxGamesInput(event.target.value)}
            inputMode="numeric"
            aria-label="Maximum games"
            className="mt-1 w-full rounded border border-line bg-page px-3 py-2"
          />
        </label>
        <button
          type="submit"
          className="rounded bg-accent px-4 py-2 text-sm text-panel disabled:opacity-60"
          disabled={busy || Boolean(parsed.error)}
        >
          {busy ? 'Analyzing…' : 'Analyze'}
        </button>
        <div className="flex flex-wrap gap-1 sm:col-span-3">
          {AUTO_ANALYZER_QUICK_TARGETS.map((target) => (
            <button
              key={target}
              type="button"
              className={`rounded border px-2 py-1 text-xs ${
                Number(targetInput.replace(/x$/i, '')) === target ? 'border-accent bg-soft' : 'border-line'
              }`}
              onClick={() => {
                setTargetInput(String(target));
                setMaxGamesInput(String(defaultMaxGames(target)));
              }}
            >
              {formatTarget(target)}
            </button>
          ))}
        </div>
      </form>

      {parsed.error ? (
        <p className="text-sm text-warn" role="alert">
          {parsed.error}
        </p>
      ) : null}
      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {busy ? <StatusMessage kind="loading">Searching historical entry and duration regions…</StatusMessage> : null}

      {result ? <AnalyzerResult report={result} detailsOpen={detailsOpen} onToggleDetails={() => setDetailsOpen((open) => !open)} /> : null}
    </div>
  );
}

function AnalyzerResult(props: {
  report: AutoAnalyzerReport;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}) {
  const { report } = props;
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-panel p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">
          {formatTarget(report.targetMultiplier)} automatic analysis
        </p>
        <p className="mt-1 text-sm text-muted">
          Maximum games allowed: {formatInteger(report.maxGames)} · {report.stakingMode === 'MARTINGALE' ? 'Martingale' : 'Fixed'} · $1
          stake · advisory only
        </p>
      </section>

      {report.verdict === 'INSUFFICIENT_HISTORICAL_EVIDENCE' && !report.primary ? (
        <StatusMessage kind="empty">
          {report.insufficientReason ??
            'INSUFFICIENT HISTORICAL EVIDENCE. There is not enough historical data to recommend a strong region.'}
        </StatusMessage>
      ) : null}

      {report.primary ? (
        <RegionCard title="Strongest region" region={report.primary} maxGames={report.maxGames} prominent />
      ) : null}

      {report.secondary.map((region, index) => (
        <RegionCard
          key={`${region.entryFrom}-${region.gamesFrom}-${index}`}
          title="Secondary region"
          region={region}
          maxGames={report.maxGames}
        />
      ))}

      {report.heatmap.cells.length > 0 ? <AnalyzerHeatmap report={report} /> : null}

      <section className="rounded border border-line bg-panel p-4">
        <button type="button" className="text-sm font-medium text-accent" onClick={props.onToggleDetails}>
          {props.detailsOpen ? 'Hide research details' : 'Research details'}
        </button>
        {props.detailsOpen ? <ResearchDetails report={report} /> : null}
      </section>
    </div>
  );
}

function RegionCard(props: { title: string; region: RegionReport; maxGames: number; prominent?: boolean }) {
  const { region } = props;
  return (
    <section className={`rounded border bg-panel p-4 ${props.prominent ? 'border-accent' : 'border-line'}`}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">{props.title}</h2>
      <dl className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted">Entry</dt>
          <dd className="text-sm font-medium">{formatRange('D', region.entryFrom, region.entryTo)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Games</dt>
          <dd className="text-sm font-medium">{formatRange('', region.gamesFrom, region.gamesTo)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Suggested</dt>
          <dd className="text-sm font-medium">
            D{region.suggested.entryDrought} / S{region.suggested.stopDrought}
            <span className="mt-0.5 block text-xs font-normal text-muted">
              {formatInteger(region.suggested.wagerCount)} of maximum {formatInteger(props.maxGames)}
            </span>
          </dd>
        </div>
      </dl>
      {region.reasons.length > 0 ? (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Why this region</h3>
          <ul className="mt-2 space-y-1 text-sm">
            {region.reasons.map((reason) => (
              <li key={reason}>✓ {reason}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Region status</h3>
        <p className={`mt-1 text-sm font-semibold ${statusClass(region.status)}`}>{region.status}</p>
        <p className="mt-1 text-sm text-muted">{region.statusReason}</p>
      </div>
    </section>
  );
}

function AnalyzerHeatmap(props: { report: AutoAnalyzerReport }) {
  const { heatmap } = props.report;
  const entries = uniqueRange(heatmap.entryFrom, heatmap.entryTo);
  const wagers = uniqueRange(heatmap.wagerFrom, heatmap.wagerTo);
  if (entries.length === 0 || wagers.length === 0 || entries.length * wagers.length > 400) {
    return (
      <p className="text-xs text-muted">
        Heatmap omitted because the searched grid is large. The suggested cell is listed above.
      </p>
    );
  }
  const lookup = new Map(
    heatmap.cells.map((cell) => [`${cell.entryDrought}:${cell.wagerCount}`, cell] as const),
  );
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Optional heatmap</h2>
      <p className="mt-1 text-xs text-muted">Outline marks the strongest region. The filled cell is the suggestion.</p>
      <div className="mt-3 overflow-x-auto">
        <table className="border-collapse text-[11px]">
          <thead>
            <tr>
              <th className="px-1 py-1 text-left text-muted">D \ W</th>
              {wagers.map((wager) => (
                <th key={wager} className="px-1 py-1 text-muted">
                  {wager}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry}>
                <th className="px-1 py-1 text-left text-muted">D{entry}</th>
                {wagers.map((wager) => {
                  const cell = lookup.get(`${entry}:${wager}`);
                  const outlined = cell?.inPrimaryRegion;
                  const suggested = cell?.isSuggested;
                  return (
                    <td
                      key={wager}
                      className={`min-w-8 px-1 py-1 text-center ${
                        suggested ? 'bg-accent text-panel' : outlined ? 'bg-soft' : ''
                      } ${outlined ? 'outline outline-1 outline-accent' : ''}`}
                      title={
                        cell
                          ? `D${cell.entryDrought}/S${cell.stopDrought} P/E ${formatProfitExposureRatio(cell.profitExposureRatio)}`
                          : undefined
                      }
                    >
                      {cell ? formatDecimal(cell.profitExposureRatio, 1) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ResearchDetails(props: { report: AutoAnalyzerReport }) {
  const suggested = props.report.details.suggested;
  const drought = props.report.details.droughtSearch;
  return (
    <div className="mt-3 space-y-3 text-sm">
      {suggested ? (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Detail label="Episodes" value={formatInteger(suggested.completedEpisodes)} />
          <Detail label="Wins" value={formatInteger(suggested.successfulEpisodes)} />
          <Detail label="Stopouts" value={formatInteger(suggested.stopOuts)} />
          <Detail label="P&L" value={formatSignedUnits(suggested.netPnl)} />
          <Detail label="ROI" value={suggested.roi === null ? '—' : formatPercent(suggested.roi, 1)} />
          <Detail label="Profit / Exposure" value={formatProfitExposureRatio(suggested.profitExposureRatio)} />
          <Detail label="Full stopout loss" value={formatSignedUnits(-suggested.fullStopoutLoss)} />
          <Detail
            label="Median cycle coverage"
            value={suggested.medianCycleCoverage === null ? '—' : `${formatDecimal(suggested.medianCycleCoverage, 2)}x`}
          />
          <Detail
            label="2x before next stopout"
            value={
              suggested.twoXCoverageBeforeNextStopoutRate === null
                ? '—'
                : formatPercent(suggested.twoXCoverageBeforeNextStopoutRate, 0)
            }
          />
          <Detail label="Max drawdown" value={formatSignedUnits(suggested.maximumDrawdown)} />
          <Detail label="Region size" value={formatInteger(props.report.details.regionSize)} />
          <Detail
            label="Martingale exposure"
            value={
              suggested.stakingMode === 'MARTINGALE'
                ? `${formatInteger(suggested.wagerCount)} games at $1 base: ${formatInteger(suggested.fullStopoutLoss)}`
                : 'Fixed $1 per game'
            }
          />
        </dl>
      ) : null}
      <p className="text-xs text-muted">
        Drought search D{formatInteger(drought.entryFrom)}–D{formatInteger(drought.entryTo)} · mean{' '}
        {formatDecimal(drought.droughtMean, 1)} · median {formatDecimal(drought.droughtMedian, 1)} · p90{' '}
        {formatDecimal(drought.droughtP90, 1)} · p99 {formatDecimal(drought.droughtP99, 1)} · max{' '}
        {formatInteger(drought.droughtMax)}
      </p>
      {props.report.details.walkForward.length > 0 ? (
        <ul className="text-xs text-muted">
          {props.report.details.walkForward.map((fold) => (
            <li key={fold.fold}>
              Fold {fold.fold}: train {formatInteger(fold.trainStartSequence)}–{formatInteger(fold.trainEndSequence)} →
              test {formatInteger(fold.testStartSequence)}–{formatInteger(fold.testEndSequence)}{' '}
              {fold.supported ? 'supported' : 'not supported'}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">Walk-forward folds were not used because the sample is short.</p>
      )}
    </div>
  );
}

function Detail(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd>{props.value}</dd>
    </div>
  );
}

function formatRange(prefix: string, from: number, to: number): string {
  if (from === to) {
    return `${prefix}${from}`;
  }
  return `${prefix}${from} - ${prefix}${to}`;
}

function statusClass(status: RegionReport['status']): string {
  if (status === 'BROKEN') {
    return 'text-negative';
  }
  if (status === 'WEAKENING') {
    return 'text-warn';
  }
  return 'text-positive';
}

function uniqueRange(from: number, to: number): number[] {
  if (!(from >= 1) || to < from) {
    return [];
  }
  const values: number[] = [];
  for (let value = from; value <= to; value += 1) {
    values.push(value);
  }
  return values;
}
